import {
  DotSpinner,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@crono-one/design-system';
import { Event, EventOutcome } from 'crono-fe-common/types/event';
import { withMeta } from 'crono-fe-common/types/mutationMeta';
import { usePatchEvent } from 'hooks/services/event/usePatchEvent';
import { useMeetingActions } from 'pages/accountDrawer/hooks/useMeetingActions';
import { OUTCOME_LABELS } from 'pages/accountDrawer/utils/meeting.constants';

interface MeetingOutcomeFieldProps {
  event?: Event | null;
  isLoading?: boolean;
}

const MeetingOutcomeField = ({
  event,
  isLoading,
}: MeetingOutcomeFieldProps) => {
  const { canEdit } = useMeetingActions(event);
  const { mutate: patchEvent, isPending, variables } = usePatchEvent();

  // While the patch is in flight the value the user just picked is what they
  // should see; once it settles the refetched event is the only source of truth.
  const outcome = isPending
    ? variables?.outcome
    : (event?.outcome ?? undefined);

  const isEditable = !isLoading && !!event && canEdit;

  return (
    <div className="flex flex-col">
      <label
        className="text-xs text-gray-700"
        htmlFor={isEditable ? 'outcome' : undefined}
      >
        Outcome
      </label>

      {isLoading && (
        <div className="h-8 rounded-lg bg-neutral-300 animate-pulse" />
      )}

      {!isLoading && !isEditable && (
        <p className="text-sm text-neutral-800">
          {outcome ? OUTCOME_LABELS[outcome] : '-'}
        </p>
      )}

      {isEditable && (
        <Select
          value={outcome}
          disabled={isPending}
          onValueChange={(value) =>
            patchEvent(
              withMeta(
                { eventId: event.id, outcome: value as EventOutcome },
                { successMessage: 'Outcome updated' },
              ),
            )
          }
        >
          <SelectTrigger id="outcome" size="sm" className="w-full">
            <SelectValue placeholder="-" />
            {isPending && <DotSpinner className="size-4 ml-auto" />}
          </SelectTrigger>
          <SelectContent className="z-50000">
            <SelectGroup>
              {Object.values(EventOutcome).map((option) => (
                <SelectItem key={option} value={option}>
                  {OUTCOME_LABELS[option]}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      )}
    </div>
  );
};

export default MeetingOutcomeField;
